import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../AuthContext";

const GroupContext = createContext();

const ACTIVE_GROUP_KEY = "active_group_slug";

export const ALL_FEATURES = [
  { key: "courtAllocation", label: "Platzeinteilung" },
  { key: "freikarten", label: "Freikarten" },
  { key: "essensmarken", label: "Essensmarken" },
  { key: "einkleidung", label: "Einkleidung" },
  { key: "billing", label: "Abrechnung" },
  { key: "comments", label: "Kommentare" },
];

export const GroupProvider = ({ children }) => {
  const { user, isSuperadmin } = useAuth();
  const [allGroups, setAllGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(true);
  // null = noch nicht bestimmt, "*" = alle Gruppen, Set<string> = explizite Slugs
  const [allowedSlugs, setAllowedSlugs] = useState(null);

  const loadGroups = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "groups"));
      const loaded = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (loaded.length === 0) {
        // Erste Gruppe automatisch anlegen
        const defaultGroup = {
          name: "Ballkinder",
          slug: "ballkinder",
          memberLabel: "Ballkinder",
          memberSingular: "Ballkind",
          features: ALL_FEATURES.map((f) => f.key),
          config: {
            daysOfWeek: [
              "11. April", "12. April", "13. April", "14. April", "15. April",
              "16. April", "17. April", "18. April", "19. April",
            ],
            maxFreikarten: 4,
            stundenlohn: 13.90,
            rateOptions: [6.95, 13.90],
            bonusOptions: [0, 6.95, 13.90, 20.85],
          },
        };
        await setDoc(doc(db, "groups", "ballkinder"), defaultGroup);
        loaded.push({ id: "ballkinder", ...defaultGroup });
      }

      setAllGroups(loaded);
    } catch (error) {
      console.error("Fehler beim Laden der Gruppen:", error);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Lade die zugelassenen Gruppen-Slugs des aktuellen Nutzers
  useEffect(() => {
    let cancelled = false;
    const loadAllowed = async () => {
      if (!user) {
        setAllowedSlugs(null);
        return;
      }
      if (isSuperadmin) {
        setAllowedSlugs("*");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "app_config", "access"));
        if (!snap.exists()) {
          if (!cancelled) setAllowedSlugs("*");
          return;
        }
        const users = snap.data().users || [];
        const email = (user.email || "").toLowerCase();
        const entry = users.find((u) => (u.email || "").toLowerCase() === email);
        if (!entry) {
          if (!cancelled) setAllowedSlugs(new Set());
          return;
        }
        const slugs = Array.isArray(entry.groupSlugs) ? entry.groupSlugs : null;
        if (!slugs || slugs.length === 0) {
          // Keine explizite Zuordnung => Zugriff auf alle Gruppen (abwaertskompatibel)
          if (!cancelled) setAllowedSlugs("*");
        } else {
          if (!cancelled) setAllowedSlugs(new Set(slugs));
        }
      } catch (error) {
        console.error("Fehler beim Laden der Gruppen-Zuordnung:", error);
        if (!cancelled) setAllowedSlugs("*");
      }
    };
    loadAllowed();
    return () => {
      cancelled = true;
    };
  }, [user, isSuperadmin]);

  const groups = useMemo(() => {
    if (allowedSlugs === null || allowedSlugs === "*") return allGroups;
    return allGroups.filter((g) => allowedSlugs.has(g.slug));
  }, [allGroups, allowedSlugs]);

  // Aktive Gruppe (neu) waehlen, sobald sich Gruppen oder Zugriffsrechte aendern
  useEffect(() => {
    if (allGroups.length === 0 || allowedSlugs === null) return;
    const visible = allowedSlugs === "*"
      ? allGroups
      : allGroups.filter((g) => allowedSlugs.has(g.slug));
    if (visible.length === 0) {
      setActiveGroup(null);
      return;
    }
    const savedSlug = localStorage.getItem(ACTIVE_GROUP_KEY);
    const saved = visible.find((g) => g.slug === savedSlug);
    const next = saved || visible[0];
    setActiveGroup(next);
    if (!saved) {
      localStorage.setItem(ACTIVE_GROUP_KEY, next.slug);
    }
  }, [allGroups, allowedSlugs]);

  const switchGroup = useCallback((slug) => {
    const allowed =
      allowedSlugs === "*" || allowedSlugs === null || (allowedSlugs && allowedSlugs.has && allowedSlugs.has(slug));
    if (!allowed) return;
    const group = allGroups.find((g) => g.slug === slug);
    if (group) {
      setActiveGroup(group);
      localStorage.setItem(ACTIVE_GROUP_KEY, slug);
    }
  }, [allGroups, allowedSlugs]);

  const isGroupAllowed = useCallback(
    (slug) =>
      allowedSlugs === "*" ||
      allowedSlugs === null ||
      (allowedSlugs instanceof Set && allowedSlugs.has(slug)),
    [allowedSlugs]
  );

  const saveGroup = async (group) => {
    await setDoc(doc(db, "groups", group.slug), group);
    await loadGroups();
  };

  const deleteGroup = async (slug) => {
    if (allGroups.length <= 1) return; // Mindestens eine Gruppe
    await deleteDoc(doc(db, "groups", slug));
    await loadGroups();
  };

  const hasFeature = (featureKey) => {
    return activeGroup?.features?.includes(featureKey) ?? false;
  };

  // Collection-Name mit Gruppen-Prefix
  const getCollectionName = (baseName) => {
    if (!activeGroup) return baseName;
    return `${activeGroup.slug}__${baseName}`;
  };

  return (
    <GroupContext.Provider
      value={{
        groups,
        allGroups,
        activeGroup,
        groupsLoading,
        switchGroup,
        saveGroup,
        deleteGroup,
        hasFeature,
        getCollectionName,
        isGroupAllowed,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};

export const useGroup = () => {
  const ctx = useContext(GroupContext);
  if (!ctx) throw new Error("useGroup must be used within GroupProvider");
  return ctx;
};
